import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { LOCAL_WIDGET_ID, Show } from '@elfsight/lib-utils';
import {
  FormProvider,
  WidgetProvider
} from '@elfsight/widget-form-builder/widget';
import { Popup } from '@elfsight/widget-modal';
import {
  WidgetBackground,
  type WidgetBackgroundProps
} from '@elfsight/widget-ui';
import {
  createReadableClass,
  getColorWithAlpha,
  getInvertedColor,
  scrollbarColoredMixin
} from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { colors } from '../../constants';
import { Form } from './form';
import { useFormPopup } from './form-popup-context';

// TODO: Перенести компонент в widget-form-builder
const POPUP_WIDTH = 500;
const SCROLLBAR_WIDTH = 10;

const { darkBlack03, black } = colors;

export const FormPopup = () => {
  const { t } = useLingui();

  const {
    widgetId,
    theme: {
      themeSettings: {
        font: { value: fontValue }
      }
    }
  } = useSettings();

  const [hasScroll, setHasScroll] = useState(false);
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  const {
    isPopupOpen,
    closePopup,
    formSettings: { fields }
  } = useFormPopup();

  const checkScroll = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollableRef.current) {
        const hasVerticalScroll =
          scrollableRef.current.scrollHeight >
          scrollableRef.current.clientHeight;

        setHasScroll(hasVerticalScroll);
      }
    });
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);

    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll, isPopupOpen]);

  return (
    <Show when={!!fields.length}>
      <WidgetProvider>
        <FormProvider widgetId={widgetId || LOCAL_WIDGET_ID}>
          <StyledPopup
            show={isPopupOpen}
            ariaLabel={t`Form Popup`}
            onCloseRequest={closePopup}
            $font={fontValue}
            closeOnOutsideClick
            withoutTransition
            withCloseControl
            closeOnEscape
            disableScroll
            withBackdrop
            shadow
            fixed
          >
            <ScrollableWrapper ref={scrollableRef}>
              <StyledWidgetBackground $hasScroll={hasScroll}>
                <Form />
              </StyledWidgetBackground>
            </ScrollableWrapper>
          </StyledPopup>
        </FormProvider>
      </WidgetProvider>
    </Show>
  );
};

interface StyledPopupProps {
  $font?: string;
}
const StyledPopup = styled(Popup).attrs<StyledPopupProps>(
  ({
    $font,
    theme: {
      popup: { fullscreen, margin, borderRadius }
    }
  }) => ({
    className: createReadableClass({ component: 'form-popup' }),
    // Используем inline стили из-за Shadow DOM
    style: {
      position: 'absolute',
      overflow: 'hidden',
      boxShadow: `0 4px 16px 0 ${darkBlack03}`,
      fontFamily: $font && `"${$font}"`,
      display: 'block',
      borderRadius
    },
    closeControlColor: getColorWithAlpha(black, 0.7),
    width: POPUP_WIDTH,
    fullscreen,
    margin
  })
)``;

const ScrollableWrapper = styled.div.attrs({
  className: createReadableClass({
    component: 'form-popup',
    element: 'scrollable-wrapper'
  })
})`
  position: relative;
  overflow: hidden auto;
  height: 100%;

  ${({
    theme: {
      popup: {
        background: { color }
      }
    }
  }) => {
    if (!color) {
      return;
    }

    const scrollbarColor = getInvertedColor(color);

    return css`
      @supports not selector(-webkit-scrollbar) {
        scrollbar-width: thin;
        scrollbar-color: ${getColorWithAlpha(scrollbarColor, 0.1)} transparent;
      }

      ${scrollbarColoredMixin(getColorWithAlpha(scrollbarColor, 0.1))};

      &::-webkit-scrollbar-thumb:hover {
        background-color: ${getColorWithAlpha(scrollbarColor, 0.3)};
      }

      &::-webkit-scrollbar-track {
        background-color: ${color};
      }
    `;
  }}
`;

interface StyledWidgetBackgroundProps extends Partial<WidgetBackgroundProps> {
  $hasScroll: boolean;
}

const StyledWidgetBackground = styled(
  WidgetBackground
).attrs<StyledWidgetBackgroundProps>(
  ({
    $hasScroll,
    theme: {
      popup: { paddingHorizontal, paddingVertical, background }
    }
  }) => ({
    className: createReadableClass({
      component: 'form-popup',
      element: 'widget-background'
    }),
    contentStyle: {
      paddingTop: paddingVertical,
      paddingBottom: paddingVertical,
      paddingLeft: paddingHorizontal,
      paddingRight: $hasScroll
        ? paddingHorizontal - SCROLLBAR_WIDTH
        : paddingHorizontal
    },
    ...background
  })
)`
  height: 100%;
  max-height: ${({
    theme: {
      popup: { margin }
    }
  }) => `calc(100vh - ${margin * 2}px)`};
`;
